import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useResourceKit } from '@resource-kit/core/react';
import { Columns3, Download, LayoutGrid, Printer, Table2 } from 'lucide-react';
import { Button } from '../primitives/Button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from '../primitives/DropdownMenu';
import QueryBuilder from '../query/QueryBuilder';
import type { ColumnSchema, Condition } from '@resource-kit/model';
import type { ColumnVisibilityHandle } from './useColumnVisibility';
import { LIST_LAYOUT, type ListLayout } from './useListLayout';

/** The table/cards switch's state and setter (#2680). */
export interface ListLayoutToggle {
  layout: ListLayout;
  onLayoutChange: (layout: ListLayout) => void;
}

export interface ListToolbarProps {
  /** Quick-filter presets rendered on the compact top row. */
  presetSlot?: ReactNode;
  searchable: boolean;
  searchPlaceholder?: string;
  /** The COMMITTED search value (the one that drives the request). */
  search: string;
  onSearchChange?: (search: string) => void;
  /** Filter-builder schema; `null` renders no builder. */
  schema: ColumnSchema[] | null;
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  visibility: ColumnVisibilityHandle;
  onExportSelected: () => void;
  exportSelectedDisabled: boolean;
  /** Absent ⇒ the list did not opt into printing and shows no control. */
  onPrint?: (scope: 'all' | 'selected') => void;
  printSelectedDisabled?: boolean;
  onExportAll?: () => void;
  exportAllDisabled?: boolean;
  /** Absent ⇒ no table/cards switch (a phone always shows cards, #2680). */
  layoutToggle?: ListLayoutToggle;
}

/**
 * The list toolbar: presets + free-text search + filter builder + column
 * show/hide + CSV export (#1801, extracted from the old QueryDataTable).
 *
 * It renders in EVERY list state — normal, first load, and error. Presets and
 * filters are the operator's recovery affordances: when a request fails the
 * way out is usually to switch a preset or drop a bad filter, so hiding the
 * toolbar behind an error card would strand them.
 *
 * The search box owns its own debounce because that is input-local state, not
 * list state: the committed value arrives as a prop and the box only decides
 * WHEN to commit the next one.
 */
export function ListToolbar({
  presetSlot,
  searchable,
  searchPlaceholder,
  search,
  onSearchChange,
  schema,
  conditions,
  onConditionsChange,
  visibility,
  onExportSelected,
  exportSelectedDisabled,
  onPrint,
  printSelectedDisabled = false,
  onExportAll,
  exportAllDisabled = false,
  layoutToggle,
}: ListToolbarProps) {
  const { t } = useResourceKit();
  const [draftSearch, setDraftSearch] = useState(search);
  // The committed term shows as a chip UNLESS the box has focus. Without
  // this, a term would crystallise mid-keystroke the moment the debounce
  // fired, yanking the text out from under the cursor.
  const [editing, setEditing] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const showSearchChip = searchable && search.length > 0 && !editing;

  // Commit the typed value after a pause.
  useEffect(() => {
    const handler = setTimeout(() => {
      if (draftSearch !== search) {
        onSearchChange?.(draftSearch);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [draftSearch, onSearchChange, search]);

  // Follow the committed value when it changes from outside (clear filters,
  // a preset click, a back-navigation).
  useEffect(() => {
    setDraftSearch(search);
  }, [search]);

  const focusSearch = () => {
    const input = searchInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  };

  // Editing the chip must NOT clear the committed search: the list would
  // jump to its unfiltered size mid-edit and jump back on the next commit.
  // `draftSearch` already holds the committed term, so focusing is enough.
  const editSearchChip = () => {
    setEditing(true);
    // The input is always mounted, so focus lands synchronously; the
    // select waits a frame for the committed term to render into it.
    searchInputRef.current?.focus();
    requestAnimationFrame(() => searchInputRef.current?.select());
  };

  const searchChip = showSearchChip ? (
    <span
      data-testid="search-chip"
      // `data-qb-chip` keeps the bar's empty-area handler off this chip.
      // Without it the click bubbles up and focuses the input by a
      // different route — identical on screen, but the chip's own edit
      // path never runs, so it is dead code no assertion can reach.
      data-qb-chip
      className="inline-flex max-w-[300px] cursor-pointer items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-2 py-0.5 text-foreground hover:bg-primary/20"
      title={t('rk.query.searchChipTitle', { term: search })}
      onClick={editSearchChip}
    >
      <span className="truncate">{t('rk.query.searchChip', { term: search })}</span>
      <button
        type="button"
        data-testid="clear-search"
        className="text-muted-foreground hover:text-destructive"
        aria-label={t('rk.query.clearSearch')}
        onClick={(event) => {
          event.stopPropagation();
          setDraftSearch('');
          onSearchChange?.('');
        }}
      >
        ×
      </button>
    </span>
  ) : undefined;

  const searchInput = searchable ? (
    <input
      ref={searchInputRef}
      data-testid="search-input"
      className="min-w-[140px] flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
      // A committed term already reads as a chip; repeating the prompt
      // beside it would suggest a second, additive search box.
      placeholder={showSearchChip ? '' : searchPlaceholder || t('rk.query.searchPlaceholder')}
      value={showSearchChip ? '' : draftSearch}
      onChange={(event) => setDraftSearch(event.target.value)}
      onFocus={() => setEditing(true)}
      onBlur={() => {
        setEditing(false);
        // Commit whatever is in the box rather than waiting out the
        // debounce — clicking away must not silently drop the term.
        if (draftSearch !== search) onSearchChange?.(draftSearch);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          setEditing(false);
          if (draftSearch !== search) onSearchChange?.(draftSearch);
        }
        if (event.key === 'Escape') {
          setDraftSearch(search);
          setEditing(false);
        }
      }}
    />
  ) : undefined;

  return (
    <div data-testid="query-toolbar">
      {/*
       * Presets keep a row of their own, rendered ONLY by the resources
       * that declare them (2 of 15). The search box used to share this
       * row, which left the other 13 resources with a permanently empty
       * half-row above the filter bar.
       */}
      {presetSlot != null && <div className="mb-3">{presetSlot}</div>}
      {/*
       * Filter builder (left) balanced against the column show/hide menu
       * + CSV export (right) on a SINGLE row. `items-start` keeps the
       * controls pinned to the top even when the QueryBuilder wraps its
       * chips onto extra lines; the left slot flexes and `shrink-0` keeps
       * the controls from being squeezed. The left slot stays present
       * (pushing the controls right) even when there is no schema.
       * Below `sm` the row stacks: the button group wraps under the
       * filter bar instead of squeezing it to a sliver on phones.
       */}
      <div
        data-testid="query-toolbar-row"
        className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
      >
        <div className="min-w-0 sm:flex-1">
          {schema ? (
            <QueryBuilder
              columns={schema}
              value={conditions}
              onChange={onConditionsChange}
              leadingSlot={searchChip}
              trailingSlot={searchInput}
              onEmptyAreaClick={focusSearch}
            />
          ) : (
            // No filter schema: the bar still hosts the search, so
            // the two layouts stay one control, not two designs.
            searchable && (
              <div
                data-testid="query-bar"
                className="flex min-h-9 w-full flex-wrap items-center gap-2 rounded-md border border-input bg-surface-1 px-3 py-1 focus-within:ring-2 focus-within:ring-ring"
                onClick={focusSearch}
              >
                {searchChip}
                {searchInput}
              </div>
            )
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:flex-nowrap">
          {layoutToggle && (
            <>
              <Button
                variant={layoutToggle.layout === LIST_LAYOUT.table ? 'secondary' : 'outline'}
                size="sm"
                className="px-2"
                data-testid="list-layout-table"
                aria-pressed={layoutToggle.layout === LIST_LAYOUT.table}
                aria-label={t('rk.table.layoutTable')}
                title={t('rk.table.layoutTable')}
                onClick={() => layoutToggle.onLayoutChange(LIST_LAYOUT.table)}
              >
                <Table2 className="h-4 w-4" />
              </Button>
              <Button
                variant={layoutToggle.layout === LIST_LAYOUT.cards ? 'secondary' : 'outline'}
                size="sm"
                className="px-2"
                data-testid="list-layout-cards"
                aria-pressed={layoutToggle.layout === LIST_LAYOUT.cards}
                aria-label={t('rk.table.layoutCards')}
                title={t('rk.table.layoutCards')}
                onClick={() => layoutToggle.onLayoutChange(LIST_LAYOUT.cards)}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="outline"
                  size="sm"
                  className="px-2"
                  aria-label={t('rk.table.columns')}
                  title={t('rk.table.toggleColumnsLabel')}
                >
                  <Columns3 className="h-4 w-4" />
                </Button>
              }
            />
            <DropdownMenuContent>
              {visibility.toggleableColumns.map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={visibility.columnVisibility[col.id] !== false}
                  onCheckedChange={(checked) =>
                    visibility.setColumnVisibility((prev) => ({
                      ...prev,
                      [col.id]: checked,
                    }))
                  }
                >
                  {col.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="outline"
                  size="sm"
                  className="px-2"
                  aria-label={t('rk.table.export')}
                  title={t('rk.table.export')}
                >
                  <Download className="h-4 w-4" />
                </Button>
              }
            />
            <DropdownMenuContent>
              <DropdownMenuItem onClick={onExportSelected} disabled={exportSelectedDisabled}>
                {t('rk.table.exportCsvSelected')}
              </DropdownMenuItem>
              {onExportAll && (
                <DropdownMenuItem onClick={onExportAll} disabled={exportAllDisabled}>
                  {t('rk.table.exportCsvAll')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {onPrint && (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-2"
                    aria-label={t('rk.print.action')}
                    title={t('rk.print.action')}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => onPrint('all')}>
                  {t('rk.print.all')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onPrint('selected')}
                  disabled={printSelectedDisabled}
                >
                  {t('rk.print.selected')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}
